/**
 * Who the person in the frame is.
 *
 * A framed meeting cannot use our own sign-in: its storage is partitioned per
 * embedding site, the session cookie is a third-party cookie, and SecureID's
 * login page does not render inside someone else's frame. So the host
 * application — itself a SecureID client — hands us the SecureID access token
 * of its signed-in user over the origin-locked bridge, and the backend
 * exchanges it (`POST /api/v1/embed/exchange`) for an ordinary inMeet session.
 * From there the app is the app: the same join, host detection, password,
 * waiting room and meeting.
 *
 * The token never travels in a URL, and nothing long-lived is stored: the
 * inMeet access token is renewed by asking the host again (`setSessionRenewer`).
 */
import { config } from '@/config/env'
import { setSessionRenewer } from '@/lib/auth-refresh'
import { useAuthStore } from '@/features/auth/store/auth.store'
import type { AuthUserModel } from '@/features/auth/models/auth.model'
import { emitToParent, requestSecureIdToken } from './embed-bridge'

/** How long the host gets to answer at load before we fall back to a guest join. */
const BOOT_AUTH_TIMEOUT_MS = 4000
/** How long it gets when renewing mid-meeting, where waiting costs nothing visible. */
const RENEW_AUTH_TIMEOUT_MS = 10000

type Exchanged = {
  accessToken: string
  expiresIn: number
  user: { id: string; email: string; name: string; avatarUrl?: string | null; role?: string }
}

async function exchange(secureIdToken: string): Promise<Exchanged | 'refused' | 'failed'> {
  try {
    const res = await fetch(`${config.apiUrl}/api/v1/embed/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ secureIdToken }),
    })
    if (res.status >= 400 && res.status < 500) return 'refused'
    if (!res.ok) return 'failed'
    return (await res.json()) as Exchanged
  } catch {
    return 'failed'
  }
}

/**
 * Establish the frame's identity. Called once, before any route renders.
 *
 * Whatever this browser stored for this embedding site last time is NOT
 * trusted: the host's signed-in user may have changed since. The host is asked
 * every time, and its answer — a user, or none — is the answer.
 */
let bootstrap: Promise<void> | null = null

export function bootstrapEmbeddedSession(): Promise<void> {
  // Once per page: React StrictMode runs the boot effect twice in development,
  // and two auth-requests would make the host mint two tokens.
  return (bootstrap ??= runBootstrap())
}

async function runBootstrap(): Promise<void> {
  const store = useAuthStore.getState()
  const token = await requestSecureIdToken(BOOT_AUTH_TIMEOUT_MS)

  if (token === undefined) {
    // The host never answered (or does not speak the protocol). Still a guest
    // join, but say so: a host downgraded silently loses host controls with
    // nothing to tell them why.
    store.clear()
    emitToParent('error', {
      code: 'AUTH_TIMEOUT',
      message: 'The host did not provide a sign-in in time; continuing as a guest.',
    })
    return
  }

  if (!token) {
    // The host answered "no signed-in user": join as a guest, exactly like a
    // signed-out visitor to the link.
    store.clear()
    return
  }

  const result = await exchange(token)
  if (result === 'refused' || result === 'failed') {
    store.clear()
    emitToParent('error', {
      code: result === 'refused' ? 'AUTH_REFUSED' : 'AUTH_UNAVAILABLE',
      message: 'Could not sign in with the provided SecureID token; continuing as a guest.',
    })
    return
  }

  store.setSession(
    {
      accessToken: result.accessToken,
      // No refresh credential in a frame — renewal goes back to the host.
      refreshToken: '',
      expiresAt: Date.now() + result.expiresIn * 1000,
    },
    { ...result.user, avatarUrl: result.user.avatarUrl ?? undefined } as AuthUserModel,
    false,
  )

  setSessionRenewer(async () => {
    const fresh = await requestSecureIdToken(RENEW_AUTH_TIMEOUT_MS)
    if (fresh === undefined) return 'retry'
    if (fresh === null) return 'reauth'
    const renewed = await exchange(fresh)
    if (renewed === 'refused') return 'reauth'
    if (renewed === 'failed') return 'retry'
    return {
      accessToken: renewed.accessToken,
      expiresIn: renewed.expiresIn,
      user: { ...renewed.user, avatarUrl: renewed.user.avatarUrl ?? undefined },
    }
  })
}
